import React from "react";
import { graphql } from "babel-plugin-relay/macro";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import moment from "moment-timezone";
import { TreeConfigurationEditDialogProps } from "../../components/Config.interfaces";
import { useFragment, useMutation } from "react-relay";
import { NotAfterDateTimeVisibilityConfigForm_TreeNodeFragment$key } from "../../../../../../../../__generated__/NotAfterDateTimeVisibilityConfigForm_TreeNodeFragment.graphql";
import { NotAfterDateTimeVisibilityConfigForm_VisibilityTreeConfigFragment$key } from "../../../../../../../../__generated__/NotAfterDateTimeVisibilityConfigForm_VisibilityTreeConfigFragment.graphql";
import { NotAfterDateTimeVisibilityConfigForm_EditMutation } from "../../../../../../../../__generated__/NotAfterDateTimeVisibilityConfigForm_EditMutation.graphql";
import { RenderConfig, ValidatedField } from "../../../../../../core/form/ValidatedField";
import { ConfigDatePicker } from "../../components/ConfigDatePicker";
import { CONFIGS_TRANSLATIONS } from "../../../../../../../i18n/config/i18n";

const CONFIGURATION_FRAGMENT = graphql`
	fragment NotAfterDateTimeVisibilityConfigForm_VisibilityTreeConfigFragment on NotAfterDateTimeVisibilityTreeConfig {
		id
		dateTime
		configType
	}
`;

const TREE_NODE_FRAGMENT = graphql`
	fragment NotAfterDateTimeVisibilityConfigForm_TreeNodeFragment on TreeNode {
		id
	}
`;

const EDIT_CONFIGURATION_MUTATION = graphql`
	mutation NotAfterDateTimeVisibilityConfigForm_EditMutation(
		$input: EditNotAfterDateTimeVisibilityTreeConfigInput!
	) {
		Admin {
			Tree {
				editNotAfterDateTimeVisibilityTreeConfig(input: $input) {
					rootNode {
						...VisibilityTreeConfigEditor_TreeNodeFragment
					}
				}
			}
		}
	}
`;

interface FormState {
	date: string;
}

interface Props extends TreeConfigurationEditDialogProps {
	configurationFragmentRef: NotAfterDateTimeVisibilityConfigForm_VisibilityTreeConfigFragment$key;
	treeNodeFragmentRef: NotAfterDateTimeVisibilityConfigForm_TreeNodeFragment$key;
	onBack?: () => void;
}

export const NotAfterDateTimeVisibilityConfigForm = ({
	configurationFragmentRef,
	treeNodeFragmentRef,
	onBack,
}: Props) => {
	const treeNode = useFragment<NotAfterDateTimeVisibilityConfigForm_TreeNodeFragment$key>(
		TREE_NODE_FRAGMENT,
		treeNodeFragmentRef,
	);
	const configuration =
		useFragment<NotAfterDateTimeVisibilityConfigForm_VisibilityTreeConfigFragment$key>(
			CONFIGURATION_FRAGMENT,
			configurationFragmentRef,
		);
	const [edit, isEditing] = useMutation<NotAfterDateTimeVisibilityConfigForm_EditMutation>(
		EDIT_CONFIGURATION_MUTATION,
	);

	const formik = useFormik<FormState>({
		enableReinitialize: true,
		initialValues: {
			date: configuration.dateTime || moment().add(1, "year").format(),
		},
		onSubmit: (values, { setSubmitting }) => {
			edit({
				variables: {
					input: {
						rootNodeId: treeNode.id,
						configId: configuration.id,
						newDateTime: values.date,
					},
				},
			});
			setSubmitting(false);
			onBack!();
		},
	});

	return (
		<>
			<form onSubmit={formik.handleSubmit} className="p-fluid">
				<ValidatedField<FormState, string>
					name={"date"}
					label={CONFIGS_TRANSLATIONS[configuration.configType]}
					className="mb-4"
					formikConfig={formik}
					component={({
						fieldName,
						fieldValue,
						updateField,
						disabled,
					}: RenderConfig<string>) => {
						return (
							<ConfigDatePicker
								name={fieldName}
								value={fieldValue}
								onUpdate={updateField}
								disabled={disabled}
								withTimeInput={true}
							/>
						);
					}}
				/>

				<Button
					disabled={Object.entries(formik.touched).length === 0 || isEditing}
					type="submit"
					label="Speichern"
					className="mt-2"
				/>

				<Button
					type="button"
					onClick={() => {
						onBack!();
					}}
					label="Zurück"
					className="p-button-secondary mt-2"
				/>
			</form>
		</>
	);
};
